import {
  ContentNode,
  ContentNodeUtils,
  ListContentNode,
  PrunedStateConfig,
  WidgetConfig,
} from '@helo/cms'

import { TypeRenderer } from '../../datatypes/TypeRenderer'
import { ListRowSkeleton } from '../../datatypes/list/ListRowSkeleton'
import { type ListLoadingPreset } from '../../datatypes/list/listLayout'
import { WidgetRenderer2 } from '../WidgetRenderer/WidgetRenderer2'
import { createWidget } from '../widget-utils'

const isResolvedContentNode = (
  contentNode: unknown,
): contentNode is ContentNode => {
  if (!contentNode || typeof contentNode !== 'object') return false

  const node = contentNode as ContentNode

  return (
    typeof node.kind === 'string' &&
    typeof node.getDataTypeConfig === 'function'
  )
}

export type RepeaterWidgetProps = {
  limit?: number
  renderItem?: WidgetConfig
  /**
   * Items can be passed directly or using the variable injection pattern.
   * `{{!some.array.variable}}`
   */
  items?: Record<string, any>[]
  isLoading?: boolean
  contentNode?: ListContentNode
  dataTypeConfigs?: PrunedStateConfig[]
  listLoadingPreset?: ListLoadingPreset
}

export const RepeaterWidget = ({
  limit,
  renderItem,
  contentNode,
  items,
  isLoading,
  dataTypeConfigs,
  listLoadingPreset,
}: RepeaterWidgetProps) => {
  if (!isResolvedContentNode(contentNode)) {
    if (isLoading || listLoadingPreset) {
      return <ListRowSkeleton preset={listLoadingPreset} />
    }
  } else if (contentNode) {
    // Apply dataTypeConfigs to contentNode if provided
    const processedContentNode =
      dataTypeConfigs && dataTypeConfigs.length > 0
        ? ContentNodeUtils.setState(dataTypeConfigs)(contentNode)
        : contentNode
    return (
      <TypeRenderer contentNode={processedContentNode} isLoading={isLoading} />
    )
  }

  if (isLoading) {
    return <ListRowSkeleton preset={listLoadingPreset} />
  }

  const renderItems = items ?? []

  const limitedListItems = renderItems.slice(0, limit)

  // defensive coding, items could be bad value.
  return limitedListItems.map((customPayload, i) => (
    <WidgetRenderer2
      widgetConfig={renderItem}
      customPayload={{
        ...customPayload,
        repeaterItem: customPayload,
        isLoading,
      }}
      key={`${i}-${isLoading}`}
    />
  ))
}

export const RepeaterWidgetDefinition = createWidget(RepeaterWidget, {
  type: 'Repeater',
  // No slots due to handling internally
  slots: [],
  unprocessedProps: ['renderItem'],
})
